import { motion } from 'framer-motion';
import handshakeImg from '../assets/business-handshake.jpeg';

export default function CTASection() {
  return (
    <section className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-hermes-900 via-hermes-800 to-warm-900" />
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(201,169,97,0.15) 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} />

      <div className="relative max-w-6xl mx-auto px-6">
        {/* ROI Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="inline-flex items-center gap-2 text-hermes-400 font-bold text-sm uppercase tracking-wider mb-4">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            ROI 預估
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-warm-100 mb-6">
            打造你的永續增長飛輪
          </h2>
          <p className="text-lg text-warm-300/60 max-w-2xl mx-auto mb-12">
            以月訪客 5,000 人估算，導入系統優化轉化率、提升客單價並啟動夥伴分銷後
          </p>

          {/* Revenue comparison cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur border border-hermes-400/10 rounded-2xl p-6"
            >
              <p className="text-warm-400/50 text-sm mb-2">導入前月營收</p>
              <p className="text-3xl font-black text-warm-100">HK$30,000</p>
              <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full w-[47%] bg-warm-400/30 rounded-full" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-center justify-center"
            >
              <div className="w-16 h-16 bg-hermes-500 rounded-full flex items-center justify-center shadow-lg shadow-hermes-500/20">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-hermes-500/20 to-hermes-600/10 backdrop-blur border border-hermes-500/30 rounded-2xl p-6"
            >
              <p className="text-hermes-400 text-sm mb-2">導入後月營收</p>
              <p className="text-3xl font-black text-warm-100">HK$63,000</p>
              <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full w-full bg-gradient-to-r from-hermes-500 to-hermes-400 rounded-full" />
              </div>
            </motion.div>
          </div>

          {/* Growth percentage */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-3 bg-green-500/10 border border-green-500/20 rounded-full px-6 py-3"
          >
            <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-green-400 font-bold text-lg">+110% 營收增長</span>
          </motion.div>
        </motion.div>

        {/* Trust elements with handshake image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20"
        >
          {/* Trust badges */}
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4">
            {[
              { icon: '🛡️', text: 'Cloudflare 全球網絡', sub: '資料庫自動備份' },
              { icon: '⚖️', text: '完全合規', sub: '一層直接推薦，非傳銷' },
              { icon: '🔒', text: '安全合規', sub: '符合香港法規' },
            ].map((item) => (
              <div key={item.text} className="bg-white/5 backdrop-blur border border-hermes-400/10 rounded-xl p-5 text-center lg:text-left lg:flex lg:items-center lg:gap-4">
                <span className="text-2xl mb-2 lg:mb-0 block lg:inline">{item.icon}</span>
                <div>
                  <p className="text-warm-100 font-semibold text-sm">{item.text}</p>
                  <p className="text-warm-400/40 text-xs mt-1">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Handshake image */}
          <div className="relative overflow-hidden rounded-2xl hidden lg:block">
            <img
              src={handshakeImg}
              alt="專業商務合作夥伴"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-hermes-900/70 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="text-white font-bold text-lg">與 JKDCoding 攜手</p>
              <p className="text-white/70 text-sm">專業團隊為你的品牌增長保駕護航</p>
            </div>
          </div>
        </motion.div>

        {/* Diagnosis section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white/5 backdrop-blur border border-hermes-400/15 rounded-3xl p-8 sm:p-12 text-center"
        >
          <h3 className="text-2xl sm:text-3xl font-black text-warm-100 mb-4">
            🚀 下一步：讓我們評估你的品牌是否適合？
          </h3>
          <p className="text-warm-300/60 mb-8 max-w-xl mx-auto">
            這套系統不是萬靈丹。我們將為您提供免費的【三維度適配度診斷】
          </p>

          {/* 3 diagnosis items */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            {[
              { num: '1', title: '產品適配度診斷', desc: '評估您的產品是否適合線上自動銷售' },
              { num: '2', title: '市場路徑評估', desc: '分析客戶透過 WhatsApp / 社交媒體購買意願' },
              { num: '3', title: 'ROI 成長預估', desc: '根據現有數據，估算導入後營收成長空間' },
            ].map((item) => (
              <div key={item.num} className="bg-white/5 rounded-xl p-5">
                <div className="w-8 h-8 bg-hermes-500 text-white rounded-full flex items-center justify-center font-bold text-sm mx-auto mb-3">
                  {item.num}
                </div>
                <h4 className="text-warm-100 font-bold text-sm mb-1">{item.title}</h4>
                <p className="text-warm-400/50 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <motion.a
            href="https://wa.me/85251164453?text=%E6%88%91%E6%83%B3%E4%BA%86%E8%A7%A3ai-shop"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-hermes-500 to-hermes-600 text-white font-bold px-10 py-5 rounded-full text-lg shadow-2xl shadow-hermes-500/20 hover:shadow-hermes-500/40 transition-shadow duration-300"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            立即 WhatsApp 聯絡我們
          </motion.a>

          {/* Company info */}
          <div className="mt-10 pt-8 border-t border-hermes-400/10">
            <p className="text-warm-100 font-bold mb-2">JKDCoding 自動銷售系統團隊</p>
            <p className="text-warm-400/40 text-sm">
              讓我們一起，開始建造你的自動印鈔機！
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
